import { h, clear, formatClock, formatTime, formatDuration } from '../ui/dom.js';
import { button, badge, toast, emptyState } from '../ui/components.js';
import { renderScriptHtml } from '../ui/rich-text.js';
import { api, ApiError, fetchBlobUrl } from '../api.js';
import { kindIcon } from '../ui/media.js';
import type { Page, PageContext } from '../page.js';
import { MEDIA_KIND_LABELS, type MediaAsset, type PulpitView } from '../types.js';

export async function pulpitPage(context: PageContext): Promise<Page> {
  const { id } = context.params;
  if (!id) return { title: 'Púlpito', fullBleed: true, body: emptyState('Ministração não informada.') };

  const view = await api.get<PulpitView>(`/api/ministrations/${id}/pulpit`);
  const current = view.current;
  const totalDays = (view.edition.days ?? []).length || 1;

  /* ---------- Estado local ---------------------------------------------- */
  let scriptScale = Number(localStorage.getItem('f2f.pulpit.scale') ?? '1');
  let abntTypography = (localStorage.getItem('f2f.pulpit.abnt') ?? '1') === '1';
  let soundEnabled = true;

  // Sincroniza o relogio local com o do servidor para todos verem o mesmo tempo.
  const serverOffset = Date.now() - new Date(view.timer.serverTime).getTime();
  const endAt = current.scheduledEnd ? new Date(current.scheduledEnd).getTime() : null;
  const startAt = current.scheduledStart ? new Date(current.scheduledStart).getTime() : Date.now();
  let mode: 'countdown' | 'elapsed' = 'countdown';
  let localStopwatchStart: number | null = null;

  const now = (): number => Date.now() - serverOffset;

  const remainingSeconds = (): number =>
    endAt === null ? current.durationMin * 60 : Math.floor((endAt - now()) / 1000);

  const elapsedSeconds = (): number =>
    localStopwatchStart !== null
      ? Math.floor((Date.now() - localStopwatchStart) / 1000)
      : Math.max(0, Math.floor((now() - startAt) / 1000));

  /* ---------- Barra superior ------------------------------------------- */
  const clockDisplay = h('div', { class: 'pulpit__clock', text: '--:--' });
  const clockLabel = h('div', { class: 'pulpit__clock-label', text: 'Tempo restante' });
  const progressText = h('div', { class: 'muted small' });

  const toggleModeButton = button('Ver decorrido', {
    variant: 'ghost',
    size: 'sm',
    onClick: () => {
      mode = mode === 'countdown' ? 'elapsed' : 'countdown';
      toggleModeButton.textContent = mode === 'countdown' ? 'Ver decorrido' : 'Ver restante';
      clockLabel.textContent = mode === 'countdown' ? 'Tempo restante' : 'Tempo decorrido';
    },
  });

  const stopwatchButton = button('Zerar cronômetro', {
    variant: 'ghost',
    size: 'sm',
    onClick: () => {
      localStopwatchStart = localStopwatchStart === null ? Date.now() : null;
      stopwatchButton.textContent =
        localStopwatchStart === null ? 'Zerar cronômetro' : 'Parar cronômetro';
      toast.info(
        localStopwatchStart === null ? 'Cronômetro pausado' : 'Cronômetro iniciado',
        'Controle independente do horário da grade.',
      );
    },
  });

  const scriptDoc = h('div', { class: 'abnt-doc script-doc' });

  const applyScriptTypography = (): void => {
    scriptDoc.classList.toggle('script-doc--plain', !abntTypography);
    scriptDoc.style.setProperty('zoom', String(scriptScale));
    localStorage.setItem('f2f.pulpit.abnt', abntTypography ? '1' : '0');
    localStorage.setItem('f2f.pulpit.scale', String(scriptScale));
  };

  const setScale = (delta: number): void => {
    scriptScale = Math.min(2.4, Math.max(0.7, Number((scriptScale + delta).toFixed(2))));
    applyScriptTypography();
  };

  renderScriptHtml(
    scriptDoc,
    view.scriptHtml,
    view.script || 'Nenhum roteiro cadastrado para esta ministração.',
  );
  applyScriptTypography();

  /* ---------- Projecao -------------------------------------------------- */
  const slidesAsset = current.media.find((asset) => asset.kind === 'SLIDES' && asset.embedUrl);

  const projection = h('div', { class: 'projection', hidden: 'true' });
  const closeProjection = (): void => {
    projection.hidden = true;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  };

  const openProjection = (): void => {
    clear(projection);

    if (slidesAsset?.embedUrl) {
      projection.append(
        h(
          'div',
          { class: 'row row--between' },
          h('div', { style: { fontSize: '18px', fontWeight: '700' }, text: slidesAsset.title }),
          button('Fechar projeção (Esc)', { variant: 'ghost', size: 'sm', onClick: closeProjection }),
        ),
        h('iframe', {
          class: 'embed',
          src: slidesAsset.embedUrl,
          style: { aspectRatio: 'auto', flex: '1', height: '80vh' },
          attrs: { allowfullscreen: 'true', title: slidesAsset.title },
        }),
      );
    } else {
      const projectionDoc = h('div', { class: 'projection__text abnt-doc' });
      renderScriptHtml(
        projectionDoc,
        view.scriptHtml,
        view.script || 'Nenhum roteiro cadastrado para projeção.',
      );

      projection.append(
        h(
          'div',
          { class: 'row row--between' },
          h('div', { style: { fontSize: '18px', fontWeight: '700' }, text: current.title }),
          button('Fechar projeção (Esc)', { variant: 'ghost', size: 'sm', onClick: closeProjection }),
        ),
        projectionDoc,
      );
    }

    projection.hidden = false;
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') closeProjection();
  };
  document.addEventListener('keydown', onKeydown);

  /* ---------- Materiais e audio ---------------------------------------- */
  const audioContainer = h('div', { class: 'stack stack--sm' });

  const loadAudio = async (asset: MediaAsset): Promise<void> => {
    if (asset.embedUrl) {
      audioContainer.append(
        h('iframe', {
          class: 'embed',
          src: asset.embedUrl,
          attrs: { allow: 'encrypted-media', title: asset.title },
        }),
      );
      return;
    }

    if (!asset.fileUrl) return;
    try {
      const url = await fetchBlobUrl(asset.fileUrl);
      const audio = h('audio', {
        controls: 'true',
        src: url,
        style: { width: '100%' },
      }) as HTMLAudioElement;
      audioContainer.append(audio);
      void audio.play().catch(() => undefined);
    } catch (error) {
      toast.error('Falha ao carregar áudio', error instanceof ApiError ? error.message : undefined);
    }
  };

  const renderAudioQuickControls = (): void => {
    clear(audioContainer);
    const sounds = current.media.filter(
      (asset) => asset.kind === 'AUDIO' || asset.kind === 'PLAYLIST' || asset.kind === 'VIDEO',
    );

    if (sounds.length === 0) {
      audioContainer.append(
        h('div', { class: 'muted small', text: 'Nenhuma trilha ou vídeo vinculado.' }),
      );
      return;
    }

    for (const asset of sounds) {
      audioContainer.append(
        button(`${kindIcon(asset.kind)} ${asset.title}`, {
          variant: 'ghost',
          size: 'sm',
          block: true,
          onClick: () => void loadAudio(asset),
        }),
      );
    }
  };

  renderAudioQuickControls();

  /* ---------- Relogio --------------------------------------------------- */
  const tick = (): void => {
    const seconds = mode === 'countdown' ? remainingSeconds() : elapsedSeconds();
    clockDisplay.textContent = formatClock(seconds);
    clockDisplay.classList.toggle('pulpit__clock--over', mode === 'countdown' && seconds < 0);

    if (mode === 'countdown') {
      const total = current.durationMin * 60;
      const used = Math.max(0, total - Math.max(0, seconds));
      progressText.textContent = `${formatTime(current.scheduledStart)} — ${formatTime(current.scheduledEnd)} · ${formatDuration(current.durationMin)} de grade · ${Math.min(100, Math.round((used / total) * 100))}% consumido`;
    } else {
      progressText.textContent = 'Cronômetro local em andamento.';
    }
  };

  const interval = setInterval(tick, 500);
  const hashAtEntry = window.location.hash;
  const watcher = setInterval(() => {
    if (window.location.hash !== hashAtEntry) {
      clearInterval(interval);
      clearInterval(watcher);
      document.removeEventListener('keydown', onKeydown);
    }
  }, 1200);

  /* ---------- Montagem -------------------------------------------------- */
  const bar = h(
    'div',
    { class: 'pulpit__bar' },
    button('← Sair do púlpito', {
      variant: 'ghost',
      size: 'sm',
      onClick: () => context.navigate(current.editionId ? `/edicoes/${current.editionId}` : '/'),
    }),
    h(
      'div',
      { style: { minWidth: '0' } },
      h(
        'div',
        { class: 'row' },
        h('div', { style: { fontWeight: '700', fontSize: '15px' }, text: current.title }),
        totalDays > 1 ? badge(`Dia ${current.dayNumber}`, 'accent') : null,
      ),
      h('div', { class: 'muted small', text: `${view.edition.name} · ${current.ministradorName ?? 'Sem ministrador'}` }),
    ),
    h('div', { class: 'spacer' }),
    h(
      'div',
      { style: { textAlign: 'right' } },
      clockLabel,
      clockDisplay,
      progressText,
    ),
    h(
      'div',
      { class: 'row' },
      toggleModeButton,
      stopwatchButton,
      button(abntTypography ? 'Leitura' : 'ABNT', {
        variant: 'ghost',
        size: 'sm',
        onClick: (event: MouseEvent) => {
          abntTypography = !abntTypography;
          applyScriptTypography();
          const target = event.currentTarget as HTMLButtonElement;
          target.textContent = abntTypography ? 'Leitura' : 'ABNT';
        },
        'aria-label': 'Alternar tipografia ABNT ou leitura',
      }),
      button('A−', { variant: 'ghost', size: 'sm', onClick: () => setScale(-0.1), 'aria-label': 'Diminuir fonte' }),
      button('A+', { variant: 'ghost', size: 'sm', onClick: () => setScale(0.1), 'aria-label': 'Aumentar fonte' }),
      button('Projetar', { size: 'sm', onClick: openProjection }),
      button('Sino', {
        variant: 'ghost',
        size: 'sm',
        onClick: () => {
          soundEnabled = !soundEnabled;
          toast.info(soundEnabled ? 'Alertas sonoros ativos' : 'Alertas sonoros silenciados');
        },
      }),
    ),
  );

  const aside = h(
    'div',
    { class: 'pulpit__aside' },
    h(
      'div',
      { class: 'card' },
      h('div', { class: 'card__header' }, h('div', { class: 'card__title', text: 'Fila da ministração' })),
      h(
        'div',
        { class: 'card__body' },
        ...view.queue.map((item) =>
          h(
            'div',
            {
              class: `pulpit__queue-item${item.id === current.id ? ' pulpit__queue-item--current' : ''}`,
            },
            h('span', { class: 'mono', text: String(item.position).padStart(2, '0') }),
            h('span', { style: { flex: '1' }, text: item.title }),
            h('span', { class: 'mono', text: formatDuration(item.durationMin) }),
          ),
        ),
        view.next
          ? h('div', {
              class: 'muted small',
              style: { marginTop: '10px' },
              text: `Próxima: ${view.next.title}`,
            })
          : h('div', { class: 'muted small', style: { marginTop: '10px' }, text: 'Última ministração do encontro.' }),
      ),
    ),
    h(
      'div',
      { class: 'card' },
      h(
        'div',
        { class: 'card__header' },
        h('div', { class: 'card__title', text: 'Trilhas e louvores' }),
        h('span', { class: 'card__hint', text: 'Disparo rápido' }),
      ),
      h('div', { class: 'card__body' }, audioContainer),
    ),
    h(
      'div',
      { class: 'card' },
      h('div', { class: 'card__header' }, h('div', { class: 'card__title', text: 'Materiais de apoio' })),
      h(
        'div',
        { class: 'card__body', style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
        ...(current.media.length === 0
          ? [h('div', { class: 'muted small', text: 'Nenhum material anexado.' })]
          : current.media.map((asset) =>
              asset.embedUrl
                ? button(`${kindIcon(asset.kind)} ${asset.title}`, {
                    variant: 'ghost',
                    size: 'sm',
                    block: true,
                    onClick: () => window.open(asset.embedUrl as string, '_blank', 'noopener'),
                  })
                : h(
                    'div',
                    { class: 'row small muted' },
                    badge(MEDIA_KIND_LABELS[asset.kind], 'info'),
                    asset.title,
                  ),
            )),
      ),
    ),
  );

  const body = h(
    'div',
    { class: 'pulpit', dataset: { theme: 'dark' }, style: { background: '#0a0b0e' } },
    bar,
    h(
      'div',
      { class: 'pulpit__body' },
      h('div', { class: 'card script-view' }, scriptDoc),
      aside,
    ),
    projection,
    h(
      'div',
      { class: 'stack stack--sm', style: { padding: '0 20px 24px' } },
      h('div', {
        class: 'faint small',
        text: 'Dica: use A− / A+ para ajustar a tipografia de leitura e "Projetar" para abrir a projeção em tela cheia.',
      }),
    ),
  );

  return {
    title: current.title,
    fullBleed: true,
    body,
  };
}
